import {useContext} from "react";
import * as React from "react";
import type {ReferenceElement} from "slate";
import {useSelected, useSlate} from "slate-react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {ReferencesContext} from "@khan/slate-contexts";

import {
    insertReference,
    type KeyDownArgs,
    type KeyDownConfig,
} from "./helpers.ts";

export const Reference = ({
    element,
    children,
    attributes,
}: {
    element: ReferenceElement;
    children: React.ReactNode;
    attributes: any;
}) => {
    const selected = useSelected();
    const referenceStyles: React.CSSProperties = {
        ...styles.reference,
        ...(selected && styles.referenceSelected),
    };
    return (
        // eslint-disable-next-line @khan/frontend/require-nltext-is-translated -- porting over from old code
        <StyledSpan style={referenceStyles} {...attributes}>
            @{element.ref}
            {children}
        </StyledSpan>
    );
};

const StyledSpan = addStyle("span");

export const ReferenceList = () => {
    const editor = useSlate();
    const {
        query: state,
        index,
        setIndex,
        filteredReferenceList,
    } = useContext(ReferencesContext);

    if (!filteredReferenceList.length) {
        return null;
    }

    return (
        <ul style={styles.referenceList}>
            {filteredReferenceList.map((reference, i) => (
                <li key={i} style={styles.referenceItem}>
                    {/* Button is used to allow onMouseEnter */}
                    <button
                        style={{
                            ...styles.referenceButton,
                            backgroundColor:
                                i ===
                                projectedIndex(
                                    index,
                                    filteredReferenceList.length,
                                )
                                    ? "#FAF4F1"
                                    : styles.referenceButton.backgroundColor,
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            insertReference(editor, reference, state.range);
                        }}
                        onMouseEnter={() => setIndex(i)}
                    >
                        <View style={styles.name}>
                            <BoldReferenceStart
                                reference={reference}
                                search={state.search}
                            />
                        </View>
                    </button>
                </li>
            ))}
        </ul>
    );
};

const BoldReferenceStart = (props: {reference: string; search: string}) => {
    const {reference, search} = props;
    if (search.length === 0) {
        return <>{reference}</>;
    }

    const matchStartIndex = reference
        .toLowerCase()
        .indexOf(search.toLowerCase());
    const referenceLength = reference.length;
    const matchEndIndex = matchStartIndex + search.length;

    return (
        <>
            {reference.slice(0, matchStartIndex)}
            <b>{reference.slice(matchStartIndex, matchEndIndex)}</b>
            {reference.slice(matchEndIndex, referenceLength)}
        </>
    );
};

const projectedIndex = (index: number, length: number) =>
    ((index % length) + length) % length;

const selectReference: (args: KeyDownArgs) => void = ({
    event,
    editor,
    references,
}) => {
    if (!editor || !references) {
        return;
    }
    const {filteredReferenceList, query: state, index} = references;
    event.preventDefault();
    insertReference(
        editor,
        filteredReferenceList[
            projectedIndex(index, filteredReferenceList.length)
        ],
        state.range,
    );
};

// eslint-disable-next-line react-refresh/only-export-components -- porting over from old code.
export const referencesKeyDownConfig: KeyDownConfig = {
    ["ArrowUp"]: ({event, references}) => {
        if (!references) {
            return;
        }
        const {index, setIndex} = references;
        event.preventDefault();
        setIndex(index - 1);
    },
    ["ArrowDown"]: ({event, references}) => {
        if (!references) {
            return;
        }
        const {index, setIndex} = references;
        event.preventDefault();
        setIndex(index + 1);
    },
    ["Enter"]: selectReference,
    ["Tab"]: selectReference,
};

const styles = {
    reference: {
        border: "1px solid #1865F241",
        borderRadius: "5px",
        padding: "1px 5px 2px",
        ":hover": {
            borderColor: "#1865F2",
            outline: "1px solid #1865F2",
            paddingTop: "0px",
            cursor: "default",
        },
        ":focus": {
            borderColor: "#1865F2",
            outline: "1px solid #1865F2",
            paddingTop: "0px",
        },
        ":active": {
            borderColor: "#1865F2",
            outline: "1px solid #1865F2",
            background: "rgba(24, 101, 242, 0.08)",
            paddingTop: "0px",
        },
    },
    referenceSelected: {
        borderColor: "#1865F2",
        outline: "1px solid #1865F2",
        background: "rgba(24, 101, 242, 0.08)",
        paddingTop: "0px",
    },
    referenceList: {
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        padding: `${spacing.xSmall_8}px 0px`,
        border: "none",
        width: "100%",
        // Note: The chat window is usually 398px wide
        maxWidth: "398px",
        boxShadow:
            "0px -3px 3px rgba(0, 0, 0, 0.05), 0px -5px 10px rgba(33, 36, 44, 0.05)",
    },
    referenceItem: {
        width: "100%",
    },
    referenceButton: {
        font: "inherit",
        backgroundColor: "white",
        border: "none",
        textAlign: "left",
        padding: `${spacing.xxxSmall_4}px ${spacing.medium_16}px`,
        cursor: "pointer",
        width: "100%",
    },
    name: {
        display: "inline-block",
        lineHeight: "20px",
    },
} as const;
